/**
 * Loads data/seed.json (bundled with the app via Vite's `?url` import) and parses
 * it through the Zod schemas in src/schemas/persistence.ts.
 *
 * Used on first run (when LocalStorage is empty) and when the user clicks Reset.
 *
 * Patch AI: added paulEtfHoldings() and paulEtfTransactions() helpers.
 * Previously these values lived as defaultEtfHoldings/defaultEtfTransactions
 * in store.ts and were applied to every fresh user, meaning brand-new
 * accounts started with Paul's actual retirement-plan balances (296 BTC
 * Grayscale shares, 37 MSBT shares) before they'd touched anything. Now
 * the store's default*Etf* return empty arrays for new users, and the
 * Paul-specific values live here, only getting applied via loadFromSeed
 * (which is super-admin-gated post-AG). Hardcoded here rather than added
 * to seed.json because the seed schema doesn't yet model ETFs; a future
 * patch could move these into the JSON if other users ever need bundled
 * starting positions.
 *
 * Patch AL: yearly repayment defaults to 0 (overrides the seed.json value)
 * and bullRallyInProjection defaults to false. Two reasons for the override
 * vs editing seed.json: (1) the workbook seed had a non-zero number that
 * doesn't match how the projection actually evolves now (no continuous
 * looping post-AL, so a yearly repayment was misleading), and (2) keeping
 * the override here means the seed.json bytes don't churn for every UI
 * default tweak. If we later remodel the long-term sim to apply
 * repayments, this override comes back out.
 */

import seedUrl from '../../../data/seed.json?url';
import { seedSchema, type SeedData } from '../../schemas/persistence.js';
import { Decimal } from '../../engine/money.js';
import type { PurchaseTransaction, Goal, EtfHolding, EtfTransaction } from '../../engine/types.js';
import type { ScenarioPlan, LoopLoanInputs, CarryInputs } from '../../state/store.js';

export async function loadSeed(): Promise<SeedData> {
  const res = await fetch(seedUrl);
  if (!res.ok) throw new Error(`Failed to fetch seed.json: HTTP ${res.status}`);
  const json = (await res.json()) as unknown;
  return seedSchema.parse(json);
}

/** Convert seed-format actual purchases into engine-typed PurchaseTransactions. */
export function seedActualsToTransactions(seed: SeedData): PurchaseTransaction[] {
  return seed.actualPurchases.map((p, i) => ({
    id: `actual-${i}`,
    date: p.date,
    usd: p.usd,
    btc: p.btc,
    category: 'manual',
    source: 'actual',
    note: p.note ?? undefined,
  }));
}

export function seedGoalsToGoals(seed: SeedData): Goal[] {
  return seed.goals.map(g => ({
    id: g.id,
    label: g.label,
    targetBtc: g.targetBtc,
    enabled: true,
  }));
}

/** Convert seed-format conservative + hopeful plans into ScenarioPlan objects. */
export function seedToScenarios(seed: SeedData): ScenarioPlan[] {
  return [
    {
      id: 'conservative',
      name: 'Conservative',
      description: 'Highly conservative: BTC appreciates at fair value, no loop loan purchases.',
      source: 'seed',
      events: seed.conservativePlan.entries.map(e => ({
        date: e.date,
        amountUsd: e.usd,
        purchaseBtc: e.btc,
        ...(e.note ? { note: e.note } : {}),
        ...(e.category ? { category: e.category } : {}),
      })),
      workbookFinalBtc: seed.conservativePlan.finalBtcBalance,
    },
    {
      id: 'hopeful',
      name: 'Hopeful',
      description: 'BTC takes 3 years from October 2025 high to form a new high, then trends to fair value.',
      source: 'seed',
      events: seed.hopefulPlan.entries.map(e => ({
        date: e.date,
        amountUsd: e.usd,
        purchaseBtc: e.btc,
        ...(e.note ? { note: e.note } : {}),
        ...(e.category ? { category: e.category } : {}),
      })),
      workbookFinalBtc: seed.hopefulPlan.finalBtcBalance,
    },
  ];
}

/** Loose typing for the seed's loopLoan field  -  schema treats it as `unknown` because
 *  the loop-loan UI is M4-scoped. We narrow it here at the loader boundary. */
interface SeedLoopLoanShape {
  btcPriceUsd: string;
  startingCollateralBtc: string;
  loanUsd: string;
  lltv: string;
  loanApr: string;
  yearlyRepaymentUsd: string;
  targetLtv: string;
  stopLoopingBeforeYear: number;
  availableCashUsd: string;
  cashUsedForRepaymentPct: string;
  athReferencePriceUsd: string;
  safetyDrawdownThreshold: string;
  outsidePurchasesByYear: Array<{ year: number; btc: string }>;
  projectionYears: number;
}

export function seedToLoopLoanInputs(seed: SeedData): LoopLoanInputs {
  const ll = seed.loopLoan as SeedLoopLoanShape;
  return {
    btcCollateral: new Decimal(ll.startingCollateralBtc),
    loanUsd: new Decimal(ll.loanUsd),
    btcPriceUsd: new Decimal(ll.btcPriceUsd),
    lltv: new Decimal(ll.lltv),
    // Patch AL: override seed value to 0. The pre-AL projection didn't
    // apply repayments anyway (projectLoopLoan never read this field),
    // and post-AL the chart treats the projection as a hold-only path
    // unless the rally toggle is on. A non-zero default here was just
    // stale UI state from the workbook era.
    yearlyRepaymentUsd: new Decimal(0),
    athReferencePriceUsd: new Decimal(ll.athReferencePriceUsd),
    availableCashUsd: new Decimal(ll.availableCashUsd),
    cashUsedForRepaymentPct: new Decimal(ll.cashUsedForRepaymentPct),
    safetyDrawdownThreshold: new Decimal(ll.safetyDrawdownThreshold),
    bullBtcPriceUsd: new Decimal(ll.btcPriceUsd).times(2),  // default: 2x current
    bullLoanIncreaseUsd: new Decimal(ll.loanUsd),  // default: same size as initial loan
    // Patch AL: rally event off by default. Toggle in the bull-market
    // section of the LoopLoan page folds the rally into the chart.
    bullRallyInProjection: false,
    targetLtv: new Decimal(ll.targetLtv),
    stopLoopingBeforeYear: ll.stopLoopingBeforeYear,
    projectionYears: ll.projectionYears,
    startDate: new Date(),
    loanApr: new Decimal(ll.loanApr),
    loanAprMode: 'live',
    repaymentFormula: 'fixed',
  };
}

interface SeedCarryShape {
  btcCollateral: string;
  initialLoanUsd: string;
  lltv: string;
  loanApr: string;
  strcDividendApr: string;
  taxRate: string;
  beginningMonthlyExpenses: string;
  expenseGrowthApr: string;
  retirementStartMonth: number;
  loanAdditionStartMonth: number;
  loanAdditionStopMonth: number;
  firstLoanAddition: string;
  loanAdditionGrowthApr: string;
  loanRepaymentStartMonth: number;
  firstLoanRepayment: string;
  loanRepaymentGrowthApr: string;
  projectionMonths: number;
}

export function seedToCarryInputs(seed: SeedData): CarryInputs {
  const c = seed.carry as SeedCarryShape;
  return {
    btcCollateral: new Decimal(c.btcCollateral),
    initialLoanUsd: new Decimal(c.initialLoanUsd),
    lltv: new Decimal(c.lltv),
    loanApr: new Decimal(c.loanApr),
    loanAprMode: 'manual',
    strcDividendApr: new Decimal(c.strcDividendApr),
    taxRate: new Decimal(c.taxRate),
    beginningMonthlyExpenses: new Decimal(c.beginningMonthlyExpenses),
    expenseGrowthApr: new Decimal(c.expenseGrowthApr),
    inflationApr: new Decimal('0.03'),
    retirementStartMonth: c.retirementStartMonth,
    loanAdditionStartMonth: c.loanAdditionStartMonth,
    loanAdditionStopMonth: c.loanAdditionStopMonth,
    firstLoanAddition: new Decimal(c.firstLoanAddition),
    loanAdditionGrowthApr: new Decimal(c.loanAdditionGrowthApr),
    loanRepaymentStartMonth: c.loanRepaymentStartMonth,
    firstLoanRepayment: new Decimal(c.firstLoanRepayment),
    loanRepaymentGrowthApr: new Decimal(c.loanRepaymentGrowthApr),
    projectionMonths: c.projectionMonths,
    // Workbook anchors carry start at row 14 of carry sheet (date 2033-09-21 04:48Z)
    startDate: new Date('2033-09-21T04:48:00Z'),
    chartWindowYears: 10,
    stressBtcPricePct: new Decimal(0),
    stressDividendCutPct: new Decimal(0),
    stressInterestShockPct: new Decimal(0),
    stressExpenseShockPct: new Decimal(0),
  };
}

/**
 * Patch AI: Paul's bitcoin-ETF retirement holdings. Hardcoded values for
 * the two trusts in his retirement account: Grayscale Bitcoin Mini Trust
 * (BTC) and Morgan Stanley Bitcoin Trust (MSBT). Pre-AI these were stored
 * as defaultEtfHoldings in store.ts and applied to every user, including
 * brand-new accounts that had no business seeing them. Now they live here
 * as Paul-seed data and only flow into the store via loadFromSeed (which
 * is super-admin-gated by App.tsx post-AG).
 *
 * Returned as a fresh array each call because the store treats persisted
 * holdings as mutable; sharing a single instance across calls would cause
 * cross-state aliasing.
 */
export function paulEtfHoldings(): EtfHolding[] {
  return [
    { id: 'etf-btc', symbol: 'BTC', name: 'Grayscale Bitcoin Mini Trust' },
    { id: 'etf-msbt', symbol: 'MSBT', name: 'Morgan Stanley Bitcoin Trust' },
  ];
}

/**
 * Patch AI: starting transactions for Paul's ETF holdings. Recorded with
 * today's date and no per-share cost basis (he doesn't have historical
 * cost data for the inherited retirement-plan balances; basis is tracked
 * forward from any new transactions he adds afterward). Same reasoning
 * as paulEtfHoldings: moved out of store.ts defaults to prevent leaking
 * into other users' fresh state.
 */
export function paulEtfTransactions(): EtfTransaction[] {
  const startDate = new Date();
  return [
    {
      id: 'etx-btc-start',
      holdingId: 'etf-btc',
      date: startDate,
      shares: new Decimal('296'),
      pricePerShareUsd: undefined,
      category: 'manual',
      note: 'Initial retirement-plan balance',
    },
    {
      id: 'etx-msbt-start',
      holdingId: 'etf-msbt',
      date: startDate,
      shares: new Decimal('37'),
      pricePerShareUsd: undefined,
      category: 'manual',
      note: 'Initial retirement-plan balance',
    },
  ];
}
